import { zodResolver } from '@hookform/resolvers/zod'
import React, { useState, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { Button } from '@/components/ui/button'
import { Sheet, SheetTrigger, SheetContent } from '@/components/ui/sheet'
import { useAppDispatch } from '@/hooks/redux'
import { SocialMediaInputs } from '@/pages/Setup/Components/SocialMediaInputs'
import { ESocialMedia } from '@/store/api/NewSearch/NewSearch.types'
import { selectUserInfo } from '@/store/api/Users/Users.selector'
import { updateSocialMedia } from '@/store/api/Users/Users.slice'
import type { TSocialMediaUser } from '@/store/api/Users/Users.types'
import { sanitizeSocialMediaUrl } from '@/utils/sanitizeSocialMediaUrl'
import type { LoadSocialMediaUrlSchemaType } from '@/utils/schemas'
import { loadSocialMediaUrlSchema } from '@/utils/schemas'

export const LoadSocialMediaSheet = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(false)
  const userInfo = useSelector(selectUserInfo)
  const socialMediaData = userInfo.socialMedias || []

  const form = useForm<LoadSocialMediaUrlSchemaType>({
    resolver: zodResolver(loadSocialMediaUrlSchema),
    defaultValues: {
      facebookUrl:
        socialMediaData?.find(
          (socialMedia) => socialMedia.name === ESocialMedia.FACEBOOK,
        )?.profileLink || '',
      instagramUrl:
        socialMediaData?.find(
          (socialMedia) => socialMedia.name === ESocialMedia.INSTAGRAM,
        )?.profileLink || '',
      linkedInUrl:
        socialMediaData?.find(
          (socialMedia) => socialMedia.name === ESocialMedia.LINKEDIN,
        )?.profileLink || '',
      tikTokUrl:
        socialMediaData?.find(
          (socialMedia) => socialMedia.name === ESocialMedia.TIKTOK,
        )?.profileLink || '',
      pinterestUrl:
        socialMediaData?.find(
          (socialMedia) => socialMedia.name === ESocialMedia.PINTEREST,
        )?.profileLink || '',
      redditUrl:
        socialMediaData?.find(
          (socialMedia) => socialMedia.name === ESocialMedia.REDDIT,
        )?.profileLink || '',
      twitterUrl:
        socialMediaData?.find(
          (socialMedia) => socialMedia.name === ESocialMedia.TWITTER,
        )?.profileLink || '',
    },
    mode: 'onBlur',
  })

  const { handleSubmit, formState, reset } = form
  const { isValid } = formState

  useEffect(() => {
    if (open) {
      reset({
        facebookUrl:
          socialMediaData?.find(
            (socialMedia) => socialMedia.name === ESocialMedia.FACEBOOK,
          )?.profileLink || '',
        instagramUrl:
          socialMediaData?.find(
            (socialMedia) => socialMedia.name === ESocialMedia.INSTAGRAM,
          )?.profileLink || '',
        linkedInUrl:
          socialMediaData?.find(
            (socialMedia) => socialMedia.name === ESocialMedia.LINKEDIN,
          )?.profileLink || '',
        tikTokUrl:
          socialMediaData?.find(
            (socialMedia) => socialMedia.name === ESocialMedia.TIKTOK,
          )?.profileLink || '',
        pinterestUrl:
          socialMediaData?.find(
            (socialMedia) => socialMedia.name === ESocialMedia.PINTEREST,
          )?.profileLink || '',
        redditUrl:
          socialMediaData?.find(
            (socialMedia) => socialMedia.name === ESocialMedia.REDDIT,
          )?.profileLink || '',
        twitterUrl:
          socialMediaData?.find(
            (socialMedia) => socialMedia.name === ESocialMedia.TWITTER,
          )?.profileLink || '',
      })
    }
  }, [open, socialMediaData, userInfo, reset])

  const onSubmitForm = handleSubmit(
    async (data: LoadSocialMediaUrlSchemaType) => {
      const facebookUrl = sanitizeSocialMediaUrl(data.facebookUrl || '')
      const instagramUrl = sanitizeSocialMediaUrl(data.instagramUrl || '')
      const linkedInUrl = sanitizeSocialMediaUrl(data.linkedInUrl || '')
      const tikTokUrl = sanitizeSocialMediaUrl(data.tikTokUrl || '')
      const pinterestUrl = sanitizeSocialMediaUrl(data.pinterestUrl || '')
      const redditUrl = sanitizeSocialMediaUrl(data.redditUrl || '')
      const twitterUrl = sanitizeSocialMediaUrl(data.twitterUrl || '')

      const socialMedias: TSocialMediaUser[] = [
        { name: ESocialMedia.FACEBOOK, profileLink: facebookUrl },
        { name: ESocialMedia.INSTAGRAM, profileLink: instagramUrl },
        { name: ESocialMedia.LINKEDIN, profileLink: linkedInUrl },
        { name: ESocialMedia.TIKTOK, profileLink: tikTokUrl },
        { name: ESocialMedia.PINTEREST, profileLink: pinterestUrl },
        { name: ESocialMedia.REDDIT, profileLink: redditUrl },
        { name: ESocialMedia.TWITTER, profileLink: twitterUrl },
      ].filter((socialMedia) => socialMedia.profileLink.trim() !== '')

      dispatch(updateSocialMedia(socialMedias))
      setOpen(false)
    },
  )

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger>
        <Button
          variant="outline"
          className="py-2 px-4 border-neutralColor30 rounded-3xl"
        >
          {t('common.edit')}
        </Button>
      </SheetTrigger>
      <SheetContent
        className="flex overflow-y-scroll justify-center bg-baseColor p-4 h-3/4"
        side="bottom"
      >
        <div className="flex flex-col items-center w-1/2 bg-baseColorHighlight rounded-2xl gap-5 h-[105vh] max-md:w-full p-4">
          <div className="flex flex-col gap-5 w-full">
            <div className="flex items-start max-md:items-center justify-between w-full max-md:flex-col max-md:gap-4">
              <h1 className="text-neutralColor text-[32px] font-medium max-md:text-2xl max-md:text-center">
                {t('setup.socialMediaProfiles.title')}
              </h1>
              <Button
                onClick={onSubmitForm}
                disabled={!isValid}
                className="w-min max-sm:w-2/3 max-md:w-1/3"
              >
                {t('common.save')}
              </Button>
            </div>

            <p className="text-neutralColor80 text-base font-normal max-md:text-sm max-md:text-center">
              {t('setup.socialMediaProfiles.enterUrl')}
            </p>
          </div>

          <FormProvider {...form}>
            <SocialMediaInputs />
          </FormProvider>
        </div>
      </SheetContent>
    </Sheet>
  )
}
